import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { getSleepPlanWindows } from '@/api/services/sleep'
import { getCalendarEvents } from '@/api/services/todo'
import { getSessions } from '@/api/services/workout'
import type { SleepPlanWindow } from '@/api/types/sleep'
import type { CalendarEvent } from '@/api/types/todo'
import type { WorkoutSessionListItem } from '@/api/types/workout'
import { useAppStore } from '@/store/useAppStore'
import { useTodoStore } from '@/store/useTodoStore'

export type IsoRange = { start: string; end: string }

const WORKOUT_LIMIT = 50

/**
 * 週ビューと日ビューで共通の予定データ (TODO・Google カレンダー・筋トレ・睡眠プラン) を取得する。
 * カレンダー・筋トレ・睡眠プランは管理者本人のデータなので、管理者のときだけ取得する
 */
export const useScheduleSources = () => {
  const loadTodos = useTodoStore((s) => s.loadTodos)
  const hasFullAccess = useAppStore((s) => s.hasFullAccess)
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([])
  const [workoutSessions, setWorkoutSessions] = useState<WorkoutSessionListItem[]>([])
  const [sleepWindows, setSleepWindows] = useState<SleepPlanWindow[]>([])

  const load = useCallback(
    async (range: IsoRange, { withSleepPlan = true }: { withSleepPlan?: boolean } = {}) => {
      // 未完了は全件、完了は範囲内のみ
      void loadTodos({ include_done: true, completed_from: range.start, completed_to: range.end })
      if (!hasFullAccess) return
      // 睡眠プランはカレンダーを読み直すので時間がかかる。他の表示を待たせない
      if (withSleepPlan) {
        getSleepPlanWindows(range.start, range.end)
          .then((res) => setSleepWindows(res.windows))
          .catch(() => setSleepWindows([]))
      }
      try {
        setCalendarEvents(await getCalendarEvents(range.start, range.end))
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'カレンダー取得に失敗')
      }
      try {
        setWorkoutSessions(await getSessions(WORKOUT_LIMIT))
      } catch {
        // 筋トレ取得失敗は無視
      }
    },
    [loadTodos, hasFullAccess],
  )

  return { calendarEvents, workoutSessions, sleepWindows, load }
}
